import React from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import {useParams} from 'react-router-dom';
import {useQuery} from "react-query";
import Loading from "../../../components/Loading";
import {GET_SUPPLIER} from "../../../utils/graphql";
import {apollo} from "../../../utils/apollo";
import InformationLine from "../../../components/InformationLine";
import {
    UserIcon,
    BuildingOfficeIcon,
    CheckCircleIcon,
    XCircleIcon,
    BanknotesIcon,
    ArrowLeftIcon,
    ExclamationCircleIcon,
    UsersIcon
} from "@heroicons/react/24/outline";
import {useTranslation} from "react-i18next";
import {Link, Navigate} from "react-router-dom";
import {cn} from "../../../utils/style";
import useAuthStore from "../../../store/auth";

function SubVendorView() {
    const {supplier_uuid} = useParams();
    const {merchant, roles, permissions} = useAuthStore();
    const {t} = useTranslation();

    const fetchSupplierQuery = useQuery({
        queryKey: ['supplier_details', supplier_uuid],
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_SUPPLIER,
                variables: {
                    supplierUuid: supplier_uuid
                }
            });
            return data?.getSupplier;
        },
        enabled: !!supplier_uuid
    });

    const hasAccess = roles?.includes("merchant") || permissions?.includes("view_suppliers");
    if (!hasAccess || merchant?.allowSubVendors === false) {
        return <Navigate to={'/dashboard'} replace/>
    }

    const supplier = fetchSupplierQuery?.data;

    return (
        <DashboardLayout>
            <div className="mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                    <Link to="/merchant/sub-vendors" className="p-2.5 rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-[#0b6a8c] hover:border-cyan-100 hover:bg-cyan-50/50 shadow-sm transition-all active:scale-95">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </Link>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2 rounded-xl bg-cyan-50 text-[#0b6a8c] shadow-sm border border-cyan-100/50">
                                <UsersIcon className="h-6 w-6" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{t('sub_vendors.details')}</h2>
                        </div>
                        <p className="text-gray-500 text-sm ml-1">{t('sub_vendors.page_description')}</p>
                    </div>
                </div>
                {supplier && (
                    <div className={cn("px-6 py-2 rounded-full font-bold text-sm shadow-sm border uppercase tracking-wider", 
                        supplier?.active 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                            : 'bg-rose-50 text-rose-700 border-rose-100'
                    )}>
                        {supplier?.active ? t('sub_vendors.active') : t('sub_vendors.inactive')}
                    </div>
                )}
            </div>

            {fetchSupplierQuery.isLoading && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
                    <Loading message={t('sub_vendors.loading')} fullHeight={false} />
                </div>
            )}

            {fetchSupplierQuery.isError && (
                <div className="bg-rose-50/50 border border-rose-100 p-8 rounded-[2.5rem] text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 mb-4">
                        <XCircleIcon className="h-8 w-8" />
                    </div>
                    <p className="text-rose-700 font-bold">{t('sub_vendors.error_loading')}</p>
                </div>
            )}

            {fetchSupplierQuery.isSuccess && !supplier && (
                <div className="bg-yellow-50/50 border border-yellow-100 p-8 rounded-[2.5rem] text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mb-4">
                        <ExclamationCircleIcon className="h-8 w-8" />
                    </div>
                    <p className="text-yellow-700 font-bold">{t('sub_vendors.not_found')}</p>
                </div>
            )}

            {fetchSupplierQuery.isSuccess && supplier && (
                <div className="space-y-8">
                    {/* Hero Header Card */}
                    <div className="relative overflow-hidden rounded-[2.5rem] shadow-sm border border-gray-100 bg-white p-8 sm:p-10">
                        <div className={cn("absolute top-0 right-0 w-64 h-64 -mr-20 -mt-20 rounded-full blur-3xl opacity-10", 
                            supplier?.active ? 'bg-emerald-400' : 'bg-rose-400'
                        )}></div>
                        
                        <div className="relative flex flex-col md:flex-row md:items-center gap-8">
                            <div className={cn("p-6 rounded-[2rem] shadow-inner flex items-center justify-center", 
                                supplier?.active ? 'bg-emerald-50/50' : 'bg-rose-50/50'
                            )}>
                                {supplier?.active ? (
                                    <CheckCircleIcon className="h-12 w-12 text-emerald-500"/>
                                ) : (
                                    <XCircleIcon className="h-12 w-12 text-rose-500"/>
                                )}
                            </div>
                            <div className="flex-1">
                                <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                                    {supplier?.supplierName}
                                </h1>
                                <div className="flex flex-wrap items-center gap-4 mt-3">
                                    <div className="flex items-center gap-2 text-sm font-bold text-gray-400 uppercase tracking-widest">
                                        <span className="h-2 w-2 rounded-full bg-gray-200"></span>
                                        {t('sub_vendors.id')}: <span className="font-mono text-gray-600 ml-1">{supplier?.supplierUuid}</span>
                                    </div>
                                    <div className="h-4 w-px bg-gray-200 hidden sm:block"></div>
                                    <div className="flex items-center gap-2 text-sm font-bold text-[#0b6a8c]">
                                        <BuildingOfficeIcon className="h-4 w-4" />
                                        {supplier?.businessName}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Supplier Info */}
                        <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-100/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                        <UserIcon className="h-5 w-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">{t('sub_vendors.supplier_information')}</h3>
                                </div>
                            </div>
                            <div className="p-8 space-y-6">
                                <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                    <InformationLine title={t('sub_vendors.table.supplierName')} value={supplier?.supplierName}/>
                                </div>
                                <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                    <InformationLine title={t('sub_vendors.table.businessName')} value={supplier?.businessName}/>
                                </div>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-100/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                        <BuildingOfficeIcon className="h-5 w-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">{t('invoice.customer_information')}</h3>
                                </div>
                            </div>
                            <div className="p-8 space-y-6">
                                <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                    <InformationLine title={t('sub_vendors.table.email')} value={supplier?.email}/>
                                </div>
                                <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                    <InformationLine title={t('sub_vendors.table.phone')} value={supplier?.phone}/>
                                </div>
                            </div>
                        </div>

                        {/* Financial Configuration */}
                        <div className="lg:col-span-2 bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-100/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                        <BanknotesIcon className="h-5 w-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">{t('sub_vendors.financial_configuration')}</h3>
                                </div>
                            </div>
                            <div className="p-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                                    <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                        <InformationLine
                                            title={t('sub_vendors.create.share_percentage')}
                                            value={supplier?.sharePercentage ? `${supplier.sharePercentage}%` : 'N/A'}
                                        />
                                    </div>
                                    <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                        <InformationLine
                                            title={t('sub_vendors.create.bank_account_holder_name')}
                                            value={supplier?.bankAccountHolderName || 'N/A'}
                                        />
                                    </div>
                                    <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                        <InformationLine
                                            title={t('sub_vendors.create.bank_account')}
                                            value={supplier?.bankAccount || 'N/A'}
                                        />
                                    </div>
                                    <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                        <InformationLine
                                            title={t('sub_vendors.create.iban')}
                                            value={supplier?.iban || 'N/A'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Settings */}
                        <div className="lg:col-span-2 bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-100/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                                        <CheckCircleIcon className="h-5 w-5" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">{t('_.settings')}</h3>
                                </div>
                            </div>
                            <div className="p-8">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                    <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                        <InformationLine
                                            title={t('sub_vendors.create.display_supplier_details')}
                                            value={supplier?.displaySupplierDetails ? t('refund.approve') : t('refund.reject')}
                                        />
                                    </div>
                                    <div className="bg-white/40 p-5 rounded-3xl border border-gray-50/50">
                                        <InformationLine
                                            title={t('sub_vendors.status')}
                                            value={
                                                <span
                                                    className={cn("inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold border uppercase tracking-wider", 
                                                        supplier?.active 
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                                            : 'bg-rose-50 text-rose-700 border-rose-100'
                                                    )}>
                                                    {supplier?.active ? t('sub_vendors.active') : t('sub_vendors.inactive')}
                                                </span>
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}

export default SubVendorView;